import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, DatePicker, Divider, Input, InputNumber, Popconfirm, Select, Table, message } from "antd";
import { SaveOutlined, PrinterOutlined, PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import "./mineralTesting.scss";
import moment from "moment";
import { getSingleMineralEntry } from "../../../modules/actions/dataEntryActions";
import { getMineralReportByEntryNo, createMineralReport, updateMineralReport } from "../../../modules/actions/mineralActions";

const { Option } = Select;
const { Column } = Table;
const { TextArea } = Input;

const DEFAULT_REMARKS = `• The reported results represent the analyzed portion of the submitted sample only.
• Due to sample heterogeneity, results may vary if a different portion of the sample is tested.
• The sample was partially or fully consumed during analysis.`;

function MineralTestingForm(props) {
    const [entryNo, setEntryNo] = useState("");
    const [sampleData, setSampleData] = useState(null);

    const [reportNo, setReportNo] = useState("");
    const [client, setClient] = useState("");
    const [sampleDescription, setSampleDescription] = useState("Mineral Sample (As Received)");
    const [sampleRefLotNo, setSampleRefLotNo] = useState("");
    const [sampleWeight, setSampleWeight] = useState("");
    const [dateOfReceipt, setDateOfReceipt] = useState(moment());
    const [dateOfAnalysis, setDateOfAnalysis] = useState(moment());
    const [natureOfTesting, setNatureOfTesting] = useState("Destructive Laboratory Analysis / Instrumental");
    const [resultsData, setResultsData] = useState([]);
    const [remarks, setRemarks] = useState(DEFAULT_REMARKS);
    const [authorizedSignatory, setAuthorizedSignatory] = useState("");

    useEffect(() => {
        if (props.match && props.match.params && props.match.params.entryNo) {
            setEntryNo(props.match.params.entryNo);
            props.getSingleMineralEntry(props.match.params.entryNo);
            props.getMineralReportByEntryNo(props.match.params.entryNo);
        }
    }, []);

    useEffect(() => {
        if (props.singleEntry) {
            const sampleData = props.singleEntry.mineralSample || props.singleEntry;
            setSampleData(sampleData);
            setClient(props.singleEntry.customerName || sampleData.client || "");
            setDateOfReceipt(moment(props.singleEntry.entryDate || new Date()));
        }
    }, [props.singleEntry]);

    useEffect(() => {
        if (props.singleMineralReport) {
            setReportNo(props.singleMineralReport.reportNo || "");
            setClient(props.singleMineralReport.client || "");
            setSampleDescription(props.singleMineralReport.sampleDescription || "Mineral Sample (As Received)");
            setSampleRefLotNo(props.singleMineralReport.sampleRefLotNo || "");
            setSampleWeight(props.singleMineralReport.sampleWeight || "");
            setDateOfReceipt(moment(props.singleMineralReport.dateOfReceipt || new Date()));
            setDateOfAnalysis(moment(props.singleMineralReport.dateOfAnalysis || new Date()));
            setNatureOfTesting(props.singleMineralReport.natureOfTesting || "Destructive Laboratory Analysis / Instrumental");
            setResultsData(props.singleMineralReport.resultsData || []);
            setRemarks(props.singleMineralReport.remarks || DEFAULT_REMARKS);
            setAuthorizedSignatory(props.singleMineralReport.authorizedSignatory || "");
        }
    }, [props.singleMineralReport]);

    const addResultRow = () => {
        const newSrNo = resultsData.length + 1;
        setResultsData([...resultsData, { srNo: newSrNo, component: "", result: "" }]);
    };

    const updateResultRow = (index, field, value) => {
        const updatedResults = [...resultsData];
        updatedResults[index][field] = value;
        setResultsData(updatedResults);
    };

    const deleteResultRow = (index) => {
        const updatedResults = resultsData.filter((_, i) => i !== index);
        const reindexedResults = updatedResults.map((r, i) => ({ ...r, srNo: i + 1 }));
        setResultsData(reindexedResults);
    };

    const handleSave = (printAfterSave) => {
        if (!sampleRefLotNo) {
            message.error("Please enter Sample Ref / Lot No");
            return;
        }
        if (!sampleWeight) {
            message.error("Please enter Sample Weight");
            return;
        }
        if (resultsData.length === 0) {
            message.error("Please add at least one result");
            return;
        }

        const obj = {
            entryNo: entryNo,
            branchCode: (props.permission && props.permission.branchCode) || "KHI",
            sampleRefLotNo: sampleRefLotNo,
            sampleWeight: sampleWeight,
            dateOfReceipt: dateOfReceipt.format("YYYY-MM-DD"),
            dateOfAnalysis: dateOfAnalysis.format("YYYY-MM-DD"),
            natureOfTesting: natureOfTesting,
            resultsData: resultsData,
            remarks: remarks,
            authorizedSignatory: authorizedSignatory,
            uploadedBy: (props.user && props.user.username) || "",
            status: "COMPLETED"
        };

        if (reportNo) {
            props.updateMineralReport(reportNo, obj, (data) => {
                message.success("Report updated successfully");
                if (printAfterSave) {
                    props.history.push(`/mineral-testing-print/${reportNo}`);
                }
            });
        } else {
            props.createMineralReport(obj, (data) => {
                message.success("Report created successfully");
                if (printAfterSave && data && data.reportNo) {
                    props.history.push(`/mineral-testing-print/${data.reportNo}`);
                }
            });
        }
    };

    return (
        <div className="mineralTestingForm">
            <div className="innerMineralTestingForm">
                <div className="headerTitle">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => props.history.push("/mineral-testing")}
                    >
                        Back
                    </Button>
                    <h2>Mineral Testing Report</h2>
                </div>

                {props.mineralReportLoading ? (
                    <div className="loaderDiv">
                        <div>Loading...</div>
                    </div>
                ) : null}

                <div className="formContent">
                    <Divider orientation="left">Sample Information</Divider>
                    <div className="infoRow">
                        <div className="infoField">
                            <label>Entry No</label>
                            <Input value={entryNo} disabled />
                        </div>
                        <div className="infoField">
                            <label>Customer</label>
                            <Input value={client} disabled />
                        </div>
                        <div className="infoField">
                            <label>Sample Type</label>
                            <Input value={sampleData && (sampleData.sampleType === "Other" ? sampleData.sampleTypeOther : sampleData.sampleType)} disabled />
                        </div>
                        <div className="infoField">
                            <label>Quantity/Weight</label>
                            <Input value={sampleData && sampleData.quantityWeight} disabled />
                        </div>
                    </div>

                    <Divider orientation="left">Report Details</Divider>
                    <div className="infoRow">
                        <div className="infoField">
                            <label>Report No</label>
                            <Input value={reportNo} disabled placeholder="Auto-generated" />
                        </div>
                        <div className="infoField">
                            <label>Client</label>
                            <Input value={client} disabled />
                        </div>
                        <div className="infoField">
                            <label>Sample Description</label>
                            <Input value={sampleDescription} disabled />
                        </div>
                    </div>

                    <div className="infoRow">
                        <div className="infoField">
                            <label>Sample Ref / Lot No</label>
                            <Input
                                value={sampleRefLotNo}
                                onChange={(e) => setSampleRefLotNo(e.target.value)}
                                placeholder="e.g., Pes/01/01"
                            />
                        </div>
                        <div className="infoField">
                            <label>Sample Weight (g)</label>
                            <Input
                                value={sampleWeight}
                                onChange={(e) => setSampleWeight(e.target.value)}
                                placeholder="e.g., 500"
                            />
                        </div>
                        <div className="infoField">
                            <label>Date of Receipt</label>
                            <DatePicker
                                value={dateOfReceipt}
                                onChange={(date) => setDateOfReceipt(date || moment())}
                                format="DD/MM/YYYY"
                                className="w100P"
                            />
                        </div>
                        <div className="infoField">
                            <label>Date of Analysis</label>
                            <DatePicker
                                value={dateOfAnalysis}
                                onChange={(date) => setDateOfAnalysis(date || moment())}
                                format="DD/MM/YYYY"
                                className="w100P"
                            />
                        </div>
                    </div>

                    <div className="infoRow">
                        <div className="infoField">
                            <label>Nature of Testing</label>
                            <Select
                                value={natureOfTesting}
                                onChange={(val) => setNatureOfTesting(val)}
                                className="w100P"
                            >
                                <Option value="Destructive Laboratory Analysis / Instrumental">Destructive Laboratory Analysis / Instrumental</Option>
                                <Option value="Instrumental Analysis">Instrumental Analysis</Option>
                                <Option value="Destructive Laboratory Analysis">Destructive Laboratory Analysis</Option>
                            </Select>
                        </div>
                    </div>

                    <Divider orientation="left">Results & Analysis</Divider>
                    <div className="resultsTable">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={addResultRow}
                            style={{ marginBottom: 10 }}
                        >
                            Add Row
                        </Button>
                        <Table
                            dataSource={resultsData}
                            pagination={false}
                            size="small"
                            rowKey="srNo"
                        >
                            <Column
                                title="Sr. No."
                                dataIndex="srNo"
                                key="srNo"
                                width={80}
                            />
                            <Column
                                title="Component / Element"
                                dataIndex="component"
                                key="component"
                                render={(text, record, index) => (
                                    <Input
                                        value={text}
                                        onChange={(e) => updateResultRow(index, "component", e.target.value)}
                                        placeholder="e.g., Cr, Mg, Fe"
                                    />
                                )}
                            />
                            <Column
                                title="Result (%)"
                                dataIndex="result"
                                key="result"
                                render={(text, record, index) => (
                                    <Input
                                        value={text}
                                        onChange={(e) => updateResultRow(index, "result", e.target.value)}
                                        placeholder="e.g., 45%"
                                    />
                                )}
                            />
                            <Column
                                title="Action"
                                key="action"
                                width={80}
                                render={(text, record, index) => (
                                    <Popconfirm
                                        title="Delete this row?"
                                        onConfirm={() => deleteResultRow(index)}
                                    >
                                        <Button danger size="small" icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                )}
                            />
                        </Table>
                    </div>

                    <Divider orientation="left">Remarks</Divider>
                    <div className="remarksSection">
                        <TextArea
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={4}
                        />
                    </div>

                    <Divider orientation="left">Authorized Signatory</Divider>
                    <div className="infoRow">
                        <div className="infoField">
                            <label>Authorized Signatory Name</label>
                            <Input
                                value={authorizedSignatory}
                                onChange={(e) => setAuthorizedSignatory(e.target.value)}
                                placeholder="Enter signatory name"
                            />
                        </div>
                    </div>

                    <div className="formActions">
                        <Button onClick={() => props.history.push("/mineral-testing")}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={() => handleSave(false)}
                            loading={props.mineralReportLoading}
                        >
                            Save
                        </Button>
                        <Button
                            type="primary"
                            icon={<PrinterOutlined />}
                            onClick={() => handleSave(true)}
                            loading={props.mineralReportLoading}
                            style={{ background: "#52c41a", borderColor: "#52c41a" }}
                        >
                            Save & Print
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => {
    const mineralStore = state.mineralStore || {};
    return {
        singleEntry: state.dataEntry.singleEntry,
        singleMineralReport: mineralStore.singleMineralReport,
        mineralReportLoading: mineralStore.mineralReportLoading,
        permission: state.authentication.permission,
        user: state.authentication.user
    };
};

const mapDispatchToProps = (dispatch) => ({
    getSingleMineralEntry: (entryNo) => dispatch(getSingleMineralEntry(entryNo)),
    getMineralReportByEntryNo: (entryNo) => dispatch(getMineralReportByEntryNo(entryNo)),
    createMineralReport: (obj, callback) => dispatch(createMineralReport(obj, callback)),
    updateMineralReport: (reportNo, obj, callback) => dispatch(updateMineralReport(reportNo, obj, callback))
});

export default connect(mapStateToProps, mapDispatchToProps)(MineralTestingForm);